import { useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";

import { toast } from "react-toastify";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Checkbox from "@mui/material/Checkbox";

import SoftButton from "components/SoftButton";

import {
  setCredentialManagementUnshareUsersModalOpen,
  setCredentialManagementUnshareAvailableUsers,
  setCredentialManagementUnshareSelectedUsers,
  setCredentials
} from "redux/actions/portal/credentialManagementActionCreator";

import DataTable from "cxfcomponents/Tables/DataTable";

import { unshareCredential } from "api/CredentialManagementApi";

import StatusBadge from "cxfcomponents/StatusBadge";
import ProductCellfromUser from "layouts/pages/users/reports/components/Reviews/ProductCell";

const CredentialManagementUnshareUsersModal = () => {
  const { user } = useAuth0();
  const {
    credentialManagementUnshareUsersModalOpen,
    credentialManagementUnshareAvailableUsers,
    credentialManagementUnshareSelectedUsers,
    selectedCredential,
    credentials
  } = useSelector((store) => store.portal.credentialManagement);
  const { users } = useSelector((store) => store.portal.users);
  const dispatch = useDispatch();

  const handleCredentialManangementUsersModalClose = () => {
    dispatch(setCredentialManagementUnshareSelectedUsers([]));
    dispatch(setCredentialManagementUnshareUsersModalOpen(false));
  };

  const handleSelectUser = (e, userId) => {
    const { checked } = e.target;

    if (checked) {
      dispatch(
        setCredentialManagementUnshareSelectedUsers([
          ...credentialManagementUnshareSelectedUsers,
          userId
        ])
      );
    } else {
      dispatch(
        setCredentialManagementUnshareSelectedUsers(
          credentialManagementUnshareSelectedUsers.filter((uId) => uId !== userId)
        )
      );
    }
  };

  const handleUnshareCredential = async () => {
    try {
      const shareCredentialResponse = await unshareCredential(
        user?.stripeId,
        selectedCredential.id,
        selectedCredential,
        credentialManagementUnshareSelectedUsers
      );

      if (shareCredentialResponse?.isSuccessful) {
        toast.success("User credential unshare successfull");
        dispatch(
          setCredentials(
            credentials.map((credential) => {
              if (credential.id === selectedCredential.id) {
                return selectedCredential;
              }
              return credential;
            }) || []
          )
        );
      } else {
        toast.error(shareCredentialResponse.message);
      }
      dispatch(setCredentialManagementUnshareSelectedUsers([]));
      handleCredentialManangementUsersModalClose();
    } catch (error) {
      console.error("share credential error:", error);
    }
  };

  const dataTableData = useMemo(() => {
    return {
      columns: [
        {
          Header: "Unshare",
          accessor: "unshare",
          disableSortBy: true,
          disableGlobalFilter: true,
          Cell: ({ value }) => {
            return (
              <Checkbox
                checked={credentialManagementUnshareSelectedUsers.some((uId) => uId === value)}
                onChange={(e) => handleSelectUser(e, value)}
              />
            );
          }
        },
        {
          Header: "users",
          accessor: "users",
          width: 500,
          disableSortBy: false,
          canGroupBy: false,
          disableGlobalFilter: true,
          defaultSortOrder: "desc"
        },
        {
          Header: "status",
          accessor: "status",
          Cell: ({ value }) => {
            return <StatusBadge status={value} />;
          },
          canGroupBy: false,
          disableGlobalFilter: true
        }
      ],
      rows: [
        ...(credentialManagementUnshareAvailableUsers?.map((user) => {
          return {
            unshare: user.auth0_id,
            users: (
              <ProductCellfromUser
                id={user.auth0_id}
                image={user.image}
                name={`${user.first_name} ${user.last_name}`}
                firstName={user.first_name}
                lastName={user.last_name}
                email={user.email_address}
                checked={true}
              />
            ),
            status: user?.status
          };
        }) ?? [])
      ]
    };
  }, [
    users,
    selectedCredential,
    credentialManagementUnshareAvailableUsers,
    credentialManagementUnshareSelectedUsers
  ]);

  useEffect(() => {
    if (users && selectedCredential) {
      const unshareUsers =
        users.filter(
          (user) =>
            user.auth0_id !== selectedCredential?.owner_id &&
            selectedCredential?.share_user?.some(
              (shareUser) => shareUser?.user_id === user.auth0_id
            ) &&
            user.status === "active"
        ) || [];

      dispatch(setCredentialManagementUnshareAvailableUsers(unshareUsers));
    }
  }, [users, selectedCredential, credentialManagementUnshareSelectedUsers]);

  return (
    <Dialog
      open={credentialManagementUnshareUsersModalOpen}
      onClose={handleCredentialManangementUsersModalClose}
      fullWidth
      PaperProps={{
        style: {
          maxWidth: "820px"
        }
      }}
    >
      <DialogTitle sx={{ marginLeft: "24px" }}>
        Unshare credential: {selectedCredential?.application?.name}
      </DialogTitle>
      <DialogContent>
        <DataTable
          table={dataTableData}
          entriesPerPage={{ defaultValue: 25 }}
          splitted={true}
          isShareUnshareModal={true}
        />
      </DialogContent>
      <DialogActions>
        <SoftButton
          onClick={handleCredentialManangementUsersModalClose}
          color="primary"
          variant="outlined"
        >
          Cancel
        </SoftButton>
        <SoftButton
          onClick={handleUnshareCredential}
          color="primary"
          disabled={credentialManagementUnshareSelectedUsers.length === 0}
        >
          Unshare
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
};

export default CredentialManagementUnshareUsersModal;
